import React, { useState, useEffect, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import FilterDropdown from '../components/common/FilterDropdown';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    Divider,
    LinearProgress,
    Tooltip,
    Avatar,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Switch,
} from '@mui/material';
import {
    AutoAwesome,
    TrendingUp,
    Inventory2,
    ArrowForward,
    Refresh,
    Star,
    AddBox,
    SmartToy,
    ImageSearch,
    Close,
    FileDownload,
    Autorenew,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { WarehouseAPI } from '../services/api';
import { usePermission } from '../hooks/usePermission';
import PageHeader from '../components/common/PageHeader';
import SegmentedFilter from '../components/common/SegmentedFilter';
import StatPill from '../components/common/StatPill';

// ------- Types -------
interface SlotFilter {
    category: string;
    subCategory: string;
}

interface ComponentItem {
    sku: string;
    name: string;
    category: string;
    subCategory: string;
    imageUrl: string | null;
    inventory: number;
    drr3Day: number;
    drr7Day: number;
    indiDrr3Day: number;
    opportunityScore: number;
}

interface BundleCombo {
    id: string;
    components: ComponentItem[];
    combinedScore: number;
    combinedDrr: number;
    canMake: number;
    suggestedName: string;
    varieties: number;
    // AI analysis fields (populated after batch AI call)
    aiScore?: number;
    aiScoreLabel?: string;
    aiBundleName?: string;
    aiWhyBundle?: string;
    aiVisualHarmony?: string;
    aiOccasion?: string;
    aiTagline?: string;
    aiError?: boolean;
}

// ------- Opportunity Score Meter -------
const OpportunityMeter: React.FC<{ score: number; maxScore: number }> = ({ score, maxScore }) => {
    const pct = maxScore > 0 ? Math.min(100, (score / maxScore) * 100) : 0;
    const color = pct >= 70 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#DC5829';
    const label = pct >= 70 ? 'High' : pct >= 40 ? 'Medium' : 'Low';
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Opportunity</Typography>
                <Typography variant="caption" fontWeight={700} sx={{ color }}>{label}</Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={pct}
                sx={{ height: 6, borderRadius: 3, bgcolor: '#F3F2EC', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: color } }}
            />
        </Box>
    );
};

// ------- Component Product Mini-Card -------
const ProductMiniCard: React.FC<{ item: ComponentItem }> = ({ item }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, p: 1.25, borderRadius: 2, bgcolor: '#FAFAF7', border: '1px solid #F3F2EC', flex: 1, minWidth: 0 }}>
        <Avatar src={item.imageUrl || undefined} variant="rounded" sx={{ width: 52, height: 52, bgcolor: 'rgba(220, 88, 41, 0.06)', border: '1px solid rgba(220, 88, 41, 0.25)', flexShrink: 0 }}>
            <Inventory2 sx={{ color: '#DC5829', fontSize: 22 }} />
        </Avatar>
        <Box sx={{ textAlign: 'center', minWidth: 0, width: '100%' }}>
            <Typography variant="caption" fontWeight={700} sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3, fontSize: '0.7rem', color: '#1A1410' }}>
                {item.name}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#1A1410', fontFamily: 'monospace', display: 'block', mt: 0.25 }}>
                {item.sku}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Chip label={`DRR ${item.drr3Day.toFixed(1)}`} size="small" sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 700, fontSize: '0.62rem', height: 20 }} />
            <Chip label={`Stk ${item.inventory.toLocaleString()}`} size="small" sx={{ bgcolor: 'rgba(220, 88, 41, 0.08)', color: '#B8451E', fontWeight: 700, fontSize: '0.62rem', height: 20 }} />
        </Box>
        {item.subCategory && (
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#DC5829', fontWeight: 600, textAlign: 'center' }}>{item.subCategory}</Typography>
        )}
    </Box>
);

// ------- Constants -------
const SLOT_COLORS = ['#DC5829', '#4A433D', '#16a34a', '#b45309', '#dc2626'];
const SLOT_LABELS = ['Item 1', 'Item 2', 'Item 3', 'Item 4', 'Item 5'];

// ------- Main Page -------
const BundleCombination: React.FC = () => {
    const navigate = useNavigate();
    const reduceMotion = useReducedMotion();
    // RBAC: the "Create This Bundle" affordance is a bundle-combination mutate entry point
    // (it hands off to the gated bundle-category create flow). Hide it for view-only
    // callers; the destination page + backend enforce their own guards (SI-5).
    const canEdit = usePermission('bundle-combination').can('edit');

    const [comboSize, setComboSize] = useState('2');
    const [slots, setSlots] = useState<SlotFilter[]>([
        { category: '', subCategory: '' },
        { category: '', subCategory: '' },
    ]);
    const [minDRR, setMinDRR] = useState('');
    const [minInventory, setMinInventory] = useState('');
    const [excludeSkusInput, setExcludeSkusInput] = useState(''); // comma-separated raw input
    const [excludeSkuTags, setExcludeSkuTags] = useState<string[]>([]); // parsed tags
    const [aiMode, setAiMode] = useState(false);
    const [aiAnalyzing, setAiAnalyzing] = useState(false);
    const [aiProgress, setAiProgress] = useState({ done: 0, total: 0 });
    const [sortBy, setSortBy] = useState<'ai' | 'combined'>('combined');

    const [filterOptions, setFilterOptions] = useState<{
        categories: string[];
        subCategories: string[];
        categorySubcategoryMap: Record<string, string[]>;
    }>({
        categories: [],
        subCategories: [],
        categorySubcategoryMap: {},
    });
    const [combos, setCombos] = useState<BundleCombo[]>([]);
    const [maxScore, setMaxScore] = useState(1);
    const [loadingFilters, setLoadingFilters] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Keep slots array in sync with combo size
    const handleComboSizeChange = (val: string) => {
        const newSize = parseInt(val);
        setComboSize(val);
        setSlots((prev) => {
            const next = [...prev];
            while (next.length < newSize) next.push({ category: '', subCategory: '' });
            return next.slice(0, newSize);
        });
    };

    const updateSlot = (idx: number, field: keyof SlotFilter, value: string) => {
        setSlots((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            if (field === 'category') next[idx].subCategory = '';
            return next;
        });
    };

    useEffect(() => {
        const fetchFilters = async () => {
            setLoadingFilters(true);
            try {
                const data = await WarehouseAPI.getBundleSuggestionFilters();
                setFilterOptions({
                    categories: data.categories,
                    subCategories: data.subCategories,
                    categorySubcategoryMap: data.categorySubcategoryMap || {},
                });
            } catch (err: any) {
                setError(err?.response?.data?.error || 'Failed to load category filters — try refreshing the page.');
            } finally {
                setLoadingFilters(false);
            }
        };
        fetchFilters();
    }, []);

    const handleSearch = useCallback(async () => {
        setLoading(true);
        setError(null);
        setSearched(true);
        try {
            const result = await WarehouseAPI.getBundleSuggestions({
                slots,
                minDRR: minDRR ? parseFloat(minDRR) : undefined,
                minInventory: minInventory ? parseFloat(minInventory) : undefined,
                comboSize: parseInt(comboSize),
                excludeSkus: excludeSkuTags.length > 0 ? excludeSkuTags : undefined,
            });
            const data: BundleCombo[] = result.data || [];
            setCombos(data);
            setMaxScore(Math.max(...data.map((c) => c.combinedScore), 1));

            // If AI mode is enabled, batch-analyze all combos in parallel then re-sort by AI score
            if (aiMode && data.length > 0) {
                setAiAnalyzing(true);
                setAiProgress({ done: 0, total: data.length });
                let doneCount = 0;
                const analyzed = await Promise.allSettled(
                    data.map(async (combo) => {
                        try {
                            const aiResult = await WarehouseAPI.analyzeBundle(
                                combo.components.map((c) => ({
                                    sku: c.sku,
                                    name: c.name,
                                    imageUrl: c.imageUrl,
                                    category: c.category,
                                    subCategory: c.subCategory,
                                }))
                            );
                            doneCount++;
                            setAiProgress((p) => ({ ...p, done: doneCount }));
                            return { ...combo, aiScore: aiResult.score, aiScoreLabel: aiResult.scoreLabel, aiBundleName: aiResult.bundleName, aiWhyBundle: aiResult.whyBundle, aiVisualHarmony: aiResult.visualHarmony, aiOccasion: aiResult.occasion, aiTagline: aiResult.tagline };
                        } catch {
                            doneCount++;
                            setAiProgress((p) => ({ ...p, done: doneCount }));
                            return { ...combo, aiError: true };
                        }
                    })
                );
                const aiCombos = analyzed.map((r) => (r.status === 'fulfilled' ? r.value : r.reason));
                // Sort by AI score descending; combos without a score go to the end
                aiCombos.sort((a: BundleCombo, b: BundleCombo) => (b.aiScore ?? -1) - (a.aiScore ?? -1));
                setCombos(aiCombos);
                setSortBy('ai');
                setAiAnalyzing(false);
            }
        } catch {
            setError('Failed to fetch suggestions. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [slots, minDRR, minInventory, comboSize, excludeSkuTags, aiMode]);

    const handleReset = () => {
        setSlots(Array.from({ length: parseInt(comboSize) }, () => ({ category: '', subCategory: '' })));
        setMinDRR('');
        setMinInventory('');
        setExcludeSkusInput('');
        setExcludeSkuTags([]);
        setAiAnalyzing(false);
        setAiProgress({ done: 0, total: 0 });
        setCombos([]);
        setSearched(false);
        setError(null);
    };

    return (
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2, pb: 3 }}>
            {/* Header */}
            <PageHeader
                icon={<AutoAwesome sx={{ color: '#DC5829', fontSize: '1.9rem' }} />}
                title="Bundle Combination"
                subtitle="Discover new gift box ideas by pairing high-DRR products together. Pick how many items, set the category for each slot, and let the data suggest the best new bundles to create."
            />

            {/* Filter Panel — house surface */}
            <Box
                component={motion.div}
                initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                sx={{ mb: 2.5, p: 3, bgcolor: '#fff', border: '1px solid #ECECE4', borderRadius: '14px', boxShadow: '0 1px 2px rgba(26,20,15,0.04)' }}
            >
                {/* Step 1 — Items per bundle segmented control */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                        Step 1 — How many items per gift box?
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <SegmentedFilter
                            options={['2', '3', '4', '5'].map((n) => ({ value: n, label: n }))}
                            value={comboSize}
                            onChange={handleComboSizeChange}
                            layoutId="bundle-combination-combo-size"
                        />
                        <Typography variant="caption" color="text.secondary">items per gift box</Typography>
                    </Box>
                </Box>

                    {/* Step 2 — Per-slot category/subcategory rows */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
                            Step 2 — Choose category for each item slot (optional)
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {slots.map((slot, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: '#FAFAF7',
                                        border: '1px solid #F3F2EC',
                                        borderLeft: `4px solid ${SLOT_COLORS[idx]}`,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {/* Slot badge */}
                                    <Box sx={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, bgcolor: SLOT_COLORS[idx], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography variant="caption" fontWeight={800} sx={{ color: '#fff', fontSize: '0.75rem' }}>{idx + 1}</Typography>
                                    </Box>
                                    <Typography variant="caption" fontWeight={700} sx={{ color: SLOT_COLORS[idx], minWidth: 44 }}>
                                        {SLOT_LABELS[idx]}
                                    </Typography>

                                    {/* Category dropdown */}
                                    <FilterDropdown
                                        label="Category"
                                        value={slot.category}
                                        onChange={(v) => updateSlot(idx, 'category', v)}
                                        allLabel="Any category"
                                        allValue=""
                                        minWidth={180}
                                        disabled={loadingFilters}
                                        options={filterOptions.categories.map((c) => ({ value: c, label: c }))}
                                    />

                                    {/* Subcategory dropdown */}
                                    <FilterDropdown
                                        label="Subcategory"
                                        value={slot.subCategory}
                                        onChange={(v) => updateSlot(idx, 'subCategory', v)}
                                        allLabel="Any subcategory"
                                        allValue=""
                                        minWidth={200}
                                        disabled={loadingFilters}
                                        options={(slot.category
                                            ? (filterOptions.categorySubcategoryMap[slot.category] || [])
                                            : filterOptions.subCategories
                                        ).map((s) => ({ value: s, label: s }))}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Step 3 — Global thresholds */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
                            Step 3 — Minimum thresholds (applied to all items)
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <TextField
                                label="Min DRR per item (3-day avg)"
                                size="small"
                                type="number"
                                inputProps={{ min: 0, step: 0.5 }}
                                value={minDRR}
                                onChange={(e) => setMinDRR(e.target.value)}
                                placeholder="e.g. 2"
                            />
                            <TextField
                                label="Min Inventory per item"
                                size="small"
                                type="number"
                                inputProps={{ min: 0, step: 10 }}
                                value={minInventory}
                                onChange={(e) => setMinInventory(e.target.value)}
                                placeholder="e.g. 100"
                            />
                        </Box>
                    </Box>

                    {/* Exclude SKUs */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                            Exclude SKUs (optional)
                        </Typography>
                        <TextField
                            size="small"
                            placeholder="Type a SKU and press Enter to exclude…"
                            value={excludeSkusInput}
                            onChange={(e) => setExcludeSkusInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ',') {
                                    e.preventDefault();
                                    const sku = excludeSkusInput.trim().replace(/,$/, '').toUpperCase();
                                    if (sku && !excludeSkuTags.includes(sku)) {
                                        setExcludeSkuTags((prev) => [...prev, sku]);
                                    }
                                    setExcludeSkusInput('');
                                } else if (e.key === 'Backspace' && excludeSkusInput === '' && excludeSkuTags.length > 0) {
                                    setExcludeSkuTags((prev) => prev.slice(0, -1));
                                }
                            }}
                            sx={{ minWidth: 320, mb: excludeSkuTags.length > 0 ? 1 : 0 }}
                        />
                        {excludeSkuTags.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                                {excludeSkuTags.map((sku) => (
                                    <Chip
                                        key={sku}
                                        label={sku}
                                        size="small"
                                        onDelete={() => setExcludeSkuTags((prev) => prev.filter((s) => s !== sku))}
                                        sx={{
                                            bgcolor: '#fef2f2',
                                            color: '#b91c1c',
                                            fontWeight: 700,
                                            fontFamily: 'monospace',
                                            fontSize: '0.72rem',
                                            border: '1px solid #fecaca',
                                            '& .MuiChip-deleteIcon': { color: '#ef4444' },
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* AI Ranking Toggle */}
                    <Box
                        sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: aiMode ? 'rgba(220, 88, 41, 0.06)' : '#FAFAF7',
                            border: `1.5px solid ${aiMode ? '#DC5829' : '#ECECE4'}`,
                            transition: 'all 0.2s',
                            cursor: 'pointer',
                        }}
                        onClick={() => setAiMode((v) => !v)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <SmartToy sx={{ color: aiMode ? '#DC5829' : '#1A1410', fontSize: 24 }} />
                                <Box>
                                    <Typography variant="body2" fontWeight={700} color={aiMode ? '#DC5829' : 'text.primary'}>
                                        AI Ranking
                                        <Chip label="GPT-4o Vision" size="small" sx={{ ml: 1, fontSize: '0.6rem', fontWeight: 700, bgcolor: aiMode ? 'rgba(220, 88, 41, 0.08)' : '#F3F2EC', color: aiMode ? '#B8451E' : '#6B625B' }} />
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Score bundles /100 by gift appeal, harmony &amp; occasion fit
                                    </Typography>
                                </Box>
                            </Box>
                            <Switch
                                checked={aiMode}
                                onChange={(e) => { e.stopPropagation(); setAiMode(e.target.checked); }}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC5829' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#DC5829' },
                                }}
                            />
                        </Box>
                    </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleSearch}
                        disabled={loading || loadingFilters}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                        sx={{ px: 4, py: 1, fontWeight: 800, textTransform: 'none', borderRadius: '999px', '&:hover': { boxShadow: '0px 4px 12px rgba(220, 88, 41, 0.3)' } }}
                    >
                        {loading ? 'Generating combos…' : 'Find Bundle Opportunities'}
                    </Button>
                    {searched && (
                        <Button
                            variant="outlined"
                            onClick={handleReset}
                            startIcon={<Refresh />}
                            sx={{ px: 2.5, py: 1, fontWeight: 700, textTransform: 'none', borderRadius: '999px' }}
                        >
                            Reset
                        </Button>
                    )}
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {/* Loading */}
            {loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                    <CircularProgress sx={{ color: '#DC5829' }} size={48} />
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>Crunching DRR data and generating combinations…</Typography>
                </Box>
            )}

            {/* AI Analysis Progress Bar */}
            {aiAnalyzing && (
                <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, bgcolor: 'rgba(220, 88, 41, 0.06)', border: '1px solid rgba(220, 88, 41, 0.25)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <CircularProgress size={18} sx={{ color: '#DC5829' }} />
                        <Typography variant="body2" fontWeight={700} color="#DC5829">
                            Analyzing with AI… {aiProgress.done} / {aiProgress.total} bundles
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={aiProgress.total > 0 ? (aiProgress.done / aiProgress.total) * 100 : 0}
                        sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(220, 88, 41, 0.08)', '& .MuiLinearProgress-bar': { bgcolor: '#DC5829', borderRadius: 4 } }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                        GPT-4o is evaluating visual harmony and gift appeal for each bundle…
                    </Typography>
                </Box>
            )}

            {/* Empty state */}
            {!loading && searched && combos.length === 0 && !error && (
                <Box sx={{ textAlign: 'center', py: 10, border: '2px dashed', borderColor: '#DAD6CC', borderRadius: 3 }}>
                    <AutoAwesome sx={{ fontSize: 56, color: '#B8B2A6', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" fontWeight={600} mb={1}>No combinations found</Typography>
                    <Typography variant="body2" color="text.secondary">Try relaxing the filters or removing minimum thresholds.</Typography>
                </Box>
            )}

            {/* Prompt state */}
            {!loading && !searched && (
                <Box sx={{ textAlign: 'center', py: 10, border: '2px dashed', borderColor: '#DAD6CC', borderRadius: 3 }}>
                    <Star sx={{ fontSize: 56, color: '#DC5829', opacity: 0.4, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" fontWeight={600} mb={1}>Discover new gift box ideas</Typography>
                    <Typography variant="body2" color="text.secondary">Choose how many items, set a category per slot (optional), then click "Find Bundle Opportunities"</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Ranked by Combined Opportunity Score = DRR × Inventory</Typography>
                </Box>
            )}

            {/* Results */}
            {!loading && combos.length > 0 && (() => {
                const sortedCombos = [...combos].sort((a, b) =>
                    sortBy === 'ai'
                        ? (b.aiScore ?? -1) - (a.aiScore ?? -1)
                        : (b.combinedScore ?? 0) - (a.combinedScore ?? 0)
                );
                return (
                    <>
                        {/* Summary stat pills — derived purely from in-memory combos */}
                        <Box
                            component={motion.div}
                            initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                            sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}
                        >
                            <StatPill variant="summary" label="Bundle Ideas" value={combos.length} accent="#DC5829" icon={<AutoAwesome fontSize="small" />} />
                            <StatPill variant="summary" label="Items per Box" value={comboSize} accent="#4A433D" icon={<Inventory2 fontSize="small" />} />
                            <StatPill variant="summary" label="Top Score" value={Math.round(maxScore).toLocaleString()} accent="#16a34a" icon={<TrendingUp fontSize="small" />} />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6" fontWeight={700}>
                                {combos.length} New Bundle Idea{combos.length !== 1 ? 's' : ''} — {comboSize}-item combos
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                    icon={<TrendingUp sx={{ fontSize: '1rem !important' }} />}
                                    label="Opportunity Score"
                                    size="small"
                                    onClick={() => setSortBy('combined')}
                                    sx={{
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        bgcolor: sortBy === 'combined' ? '#DC5829' : 'rgba(220, 88, 41, 0.06)',
                                        color: sortBy === 'combined' ? '#fff' : '#DC5829',
                                        border: '1px solid rgba(220, 88, 41, 0.25)',
                                        '&:hover': { bgcolor: sortBy === 'combined' ? '#B8451E' : 'rgba(220, 88, 41, 0.08)' },
                                    }}
                                />
                                <Chip
                                    icon={<SmartToy sx={{ fontSize: '1rem !important', color: sortBy === 'ai' ? '#fff' : '#DC5829' }} />}
                                    label="AI Score"
                                    size="small"
                                    onClick={() => setSortBy('ai')}
                                    disabled={!combos.some(c => c.aiScore !== undefined)}
                                    sx={{
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        bgcolor: sortBy === 'ai' ? '#DC5829' : 'rgba(220, 88, 41, 0.06)',
                                        color: sortBy === 'ai' ? '#fff' : '#DC5829',
                                        border: '1px solid rgba(220, 88, 41, 0.25)',
                                        '&:hover': { bgcolor: sortBy === 'ai' ? '#B8451E' : 'rgba(220, 88, 41, 0.08)' },
                                        '&.Mui-disabled': { opacity: 0.4 },
                                    }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' }, gap: 2.5 }}>
                            {sortedCombos.map((combo, idx) => (
                                <ComboCard key={combo.id} combo={combo} rank={idx + 1} maxScore={maxScore} canCreate={canEdit} onCreateBundle={() => navigate('/bundle-categories')} />
                            ))}
                        </Box>
                    </>
                );
            })()
            }
        </Box>
    );
};

// ------- Combo Card -------
interface ComboCardProps {
    combo: BundleCombo;
    rank: number;
    maxScore: number;
    canCreate: boolean;
    onCreateBundle: () => void;
}

const ComboCard: React.FC<ComboCardProps> = ({ combo, rank, maxScore, canCreate, onCreateBundle }) => {
    const navigate = useNavigate();
    const isTopPick = rank <= 5;
    const [previewState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
    const [previewUrl] = useState<string | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const handleGeneratePreview = () => {
        // Navigate to the Image Generation tool, passing the bundle's SKUs via state
        navigate('/image-generation', {
            state: {
                prefillSkus: combo.components.map(c => c.sku)
            }
        });
    };

    return (
        <>
            <Card sx={{ position: 'relative', overflow: 'hidden', bgcolor: '#fff', borderRadius: '14px', border: '1px solid', borderColor: isTopPick ? 'rgba(220, 88, 41, 0.35)' : '#ECECE4', boxShadow: '0 1px 2px rgba(26,20,15,0.04)', transition: 'box-shadow 0.2s ease, transform 0.2s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 20px rgba(26,20,15,0.10)' } }}>
                <CardContent sx={{ p: 2.5 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                            {isTopPick && (
                                <Chip icon={<Star sx={{ fontSize: '0.85rem !important', color: '#f59e0b !important' }} />} label="Top Pick" size="small" sx={{ bgcolor: '#fffbeb', color: '#b45309', fontWeight: 700, fontSize: '0.68rem', mb: 0.75, border: '1px solid #fde68a' }} />
                            )}
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                                {combo.components.length}-Item Gift Bundle Idea
                            </Typography>
                        </Box>
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: rank <= 3 ? '#DC5829' : '#F3F2EC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Typography variant="caption" fontWeight={800} sx={{ color: rank <= 3 ? '#fff' : '#1A1410', fontSize: '0.68rem' }}>
                                #{rank}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Component products */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        {combo.components.map((item) => (
                            <ProductMiniCard key={item.sku} item={item} />
                        ))}
                    </Box>

                    <Divider sx={{ mb: 2, borderColor: '#F3F2EC' }} />

                    {/* Stats */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 2 }}>
                        <StatBox icon="🚀" label="Combined DRR" value={`${combo.combinedDrr.toFixed(1)}/d`} highlight />
                        <StatBox icon="📦" label="Can Make" value={combo.canMake.toLocaleString()} highlight={combo.canMake > 50} tooltip="How many gift boxes you can assemble (limited by lowest-stock item)" />
                        <StatBox icon="🎨" label="Variety" value={`${combo.varieties} type${combo.varieties !== 1 ? 's' : ''}`} />
                    </Box>

                    <Box sx={{ mb: 2.5 }}>
                        <OpportunityMeter score={combo.combinedScore} maxScore={maxScore} />
                    </Box>

                    {/* AI Analysis Panel */}
                    {(combo.aiScore !== undefined || combo.aiError) && (
                        <Box sx={{
                            mb: 2,
                            p: 1.75,
                            borderRadius: 2,
                            bgcolor: combo.aiError ? '#fef2f2' : 'rgba(220, 88, 41, 0.06)',
                            border: `1px solid ${combo.aiError ? '#fecaca' : 'rgba(220, 88, 41, 0.25)'}`,
                        }}>
                            {combo.aiError ? (
                                <Typography variant="caption" color="error">AI analysis unavailable for this bundle.</Typography>
                            ) : (
                                <>
                                    {/* Bundle name suggested by AI */}
                                    {combo.aiBundleName && (
                                        <Typography variant="body2" fontWeight={800} sx={{ mb: 0.75, color: '#4c1d95', letterSpacing: '-0.01em' }}>
                                            🎁 {combo.aiBundleName}
                                        </Typography>
                                    )}

                                    {/* Score badge + occasion */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                        <SmartToy sx={{ fontSize: 15, color: '#DC5829' }} />
                                        <Chip
                                            label={`${combo.aiScore}/100 · ${combo.aiScoreLabel}`}
                                            size="small"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: '0.7rem',
                                                bgcolor: combo.aiScore! >= 80 ? '#dcfce7' : combo.aiScore! >= 60 ? 'rgba(220, 88, 41, 0.1)' : combo.aiScore! >= 40 ? '#fef9c3' : '#fee2e2',
                                                color: combo.aiScore! >= 80 ? '#16a34a' : combo.aiScore! >= 60 ? '#B8451E' : combo.aiScore! >= 40 ? '#a16207' : '#b91c1c',
                                            }}
                                        />
                                        {combo.aiOccasion && (
                                            <Chip label={`✨ ${combo.aiOccasion}`} size="small" sx={{ fontSize: '0.65rem', bgcolor: 'rgba(220, 88, 41, 0.06)', color: '#b8451e', fontWeight: 600 }} />
                                        )}
                                    </Box>
                                    {combo.aiTagline && (
                                        <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', fontWeight: 600, color: '#6d28d9', mb: 0.75 }}>
                                            "{combo.aiTagline}"
                                        </Typography>
                                    )}
                                    {combo.aiWhyBundle && (
                                        <Typography variant="caption" sx={{ display: 'block', color: '#374151', lineHeight: 1.5, mb: 0.5 }}>
                                            💬 {combo.aiWhyBundle}
                                        </Typography>
                                    )}
                                    {combo.aiVisualHarmony && (
                                        <Typography variant="caption" sx={{ display: 'block', color: '#6B625B', fontStyle: 'italic' }}>
                                            👁 {combo.aiVisualHarmony}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                    )}

                    {/* Generate Visual Button */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        {previewState === 'done' ? (
                            <>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    startIcon={<ImageSearch />}
                                    onClick={() => setPreviewOpen(true)}
                                    sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '999px' }}
                                >
                                    View Visual
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    startIcon={<Autorenew />}
                                    onClick={handleGeneratePreview}
                                    sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '999px', color: '#6B625B', borderColor: '#ECECE4', '&:hover': { bgcolor: '#FAFAF7', borderColor: '#8A8078' }, fontSize: '0.75rem' }}
                                >
                                    Regenerate
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                startIcon={previewState === 'loading' ? <CircularProgress size={14} /> : <ImageSearch />}
                                disabled={previewState === 'loading'}
                                onClick={handleGeneratePreview}
                                sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '999px' }}
                            >
                                {previewState === 'loading' ? 'Generating…' : previewState === 'error' ? 'Retry Visual' : '🎨 Generate Visual'}
                            </Button>
                        )}
                    </Box>
                    {previewState === 'error' && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
                            Image generation failed. Check your OpenAI key or try again.
                        </Typography>
                    )}

                    {canCreate && (
                        <Button
                            variant={isTopPick ? 'contained' : 'outlined'}
                            disableElevation
                            fullWidth size="small"
                            startIcon={<AddBox />}
                            endIcon={<ArrowForward />}
                            onClick={onCreateBundle}
                            sx={{ fontWeight: 800, textTransform: 'none', borderRadius: '999px' }}
                        >
                            Create This Bundle
                        </Button>
                    )}
                </CardContent>

                {isTopPick && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 3, bgcolor: '#DC5829' }} />
                )}
            </Card>

            {/* DALL-E Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageSearch sx={{ color: '#DC5829' }} />
                        <Typography fontWeight={700}>AI Bundle Visual</Typography>
                        <Chip label="gpt-image-1" size="small" sx={{ bgcolor: 'rgba(220, 88, 41, 0.06)', color: '#B8451E', fontWeight: 700, fontSize: '0.65rem' }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {previewUrl && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FileDownload />}
                                onClick={() => {
                                    const a = document.createElement('a');
                                    a.href = previewUrl;
                                    a.download = `bundle-visual-${combo.id}.png`;
                                    a.click();
                                }}
                                sx={{
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    borderRadius: '999px',
                                    fontSize: '0.8rem',
                                }}
                            >
                                Download
                            </Button>
                        )}
                        <IconButton onClick={() => setPreviewOpen(false)} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {previewUrl && (
                        <Box>
                            <Box
                                component="img"
                                src={previewUrl}
                                alt="AI-generated bundle visual"
                                sx={{ width: '100%', display: 'block', borderRadius: '0 0 4px 4px' }}
                            />
                            <Box sx={{ p: 2, bgcolor: '#FAFAF7' }}>
                                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                                    {combo.components.length}-Item Gift Bundle
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {combo.components.map((c) => c.name).join(' + ')}
                                </Typography>
                                {combo.aiTagline && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: '#B8451E', fontWeight: 600 }}>
                                        ✨ "{combo.aiTagline}"
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

// ------- Stat Box -------
const StatBox: React.FC<{ icon: string; label: string; value: string; highlight?: boolean; tooltip?: string }> = ({ icon, label, value, highlight, tooltip }) => {
    const inner = (
        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, bgcolor: highlight ? 'rgba(220, 88, 41, 0.06)' : '#FAFAF7', border: '1px solid', borderColor: highlight ? 'rgba(220, 88, 41, 0.25)' : '#F3F2EC', cursor: tooltip ? 'help' : 'default' }}>
            <Typography sx={{ fontSize: '1.1rem', mb: 0.25 }}>{icon}</Typography>
            <Typography variant="subtitle2" fontWeight={800} sx={{ color: highlight ? '#B8451E' : '#1A1410', lineHeight: 1, fontSize: '0.82rem' }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</Typography>
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip}>{inner}</Tooltip> : inner;
};

export default BundleCombination;
